<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useChatbotsStore } from 'dashboard/stores/chatbots';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ChatbotsAPI from 'dashboard/api/chatbots';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ChatbotConnectors from './ChatbotConnectors.vue';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const chatbotsStore = useChatbotsStore();
const { isAdmin } = useAdmin();
const currentUser = useMapGetter('getCurrentUser');

// The Advanced section is platform-operator territory, not account territory:
// `isAdmin` is the account-scoped role and is deliberately not the check here.
const isSuperAdmin = computed(() => currentUser.value.type === 'SuperAdmin');

const dialogRef = ref(null);
const editingId = ref(null);
const showAdvanced = ref(false);
// The chatbot's own current bot user when editing — not part of the
// assignable list the server returns, since the server already counts it as
// claimed. Merged back in locally so it still shows as the selection.
const currentBotUser = ref(null);

const isEditing = computed(() => editingId.value !== null);

// Guards against a stale fetch landing in the wrong dialog state if the
// user closes and reopens (or switches which chatbot they're editing)
// before the previous lookup resolves.
let tenantBotConfigRequestId = 0;

const botUserOptions = computed(() => {
  const assignable = chatbotsStore.assignableBotUsers;
  if (!currentBotUser.value) return assignable;
  if (assignable.some(user => user.id === currentBotUser.value.id)) {
    return assignable;
  }
  return [currentBotUser.value, ...assignable];
});

// Behaviour values mirror the appsmith dashboard form. They are sent verbatim
// to the bot, so the option *values* stay in Spanish; only the displayed
// labels are localised (via the *_LABEL_KEYS maps below).
const PERSONALITY_OPTIONS = ['Amigable y Cercano', 'Profesional y Ejecutivo'];
const EMOJI_OPTIONS = ['Nada', 'Moderado', 'Muchos'];
const TREATMENT_OPTIONS = ['Informal', 'Formal'];

const PERSONALITY_LABEL_KEYS = {
  'Amigable y Cercano': 'CHATBOTS.DIALOG.PERSONALITY_OPTIONS.FRIENDLY',
  'Profesional y Ejecutivo': 'CHATBOTS.DIALOG.PERSONALITY_OPTIONS.PROFESSIONAL',
};
const EMOJI_LABEL_KEYS = {
  Nada: 'CHATBOTS.DIALOG.EMOJI_OPTIONS.NONE',
  Moderado: 'CHATBOTS.DIALOG.EMOJI_OPTIONS.MODERATE',
  Muchos: 'CHATBOTS.DIALOG.EMOJI_OPTIONS.MANY',
};
const TREATMENT_LABEL_KEYS = {
  Informal: 'CHATBOTS.DIALOG.TREATMENT_OPTIONS.INFORMAL',
  Formal: 'CHATBOTS.DIALOG.TREATMENT_OPTIONS.FORMAL',
};

const emptyForm = () => ({
  botName: '',
  botFunction: '',
  personality: PERSONALITY_OPTIONS[0],
  emojiUsage: EMOJI_OPTIONS[0],
  customerTreatment: TREATMENT_OPTIONS[0],
  mainObjective: '',
  valueProposition: '',
  restrictions: '',
  botProcedure: '',
  schedulingUrl: '',
  connectors: [],
  botUserId: null,
});

const form = reactive(emptyForm());

const isFormInvalid = computed(() => !form.botName.trim());

// The bot user's access token, fetched only on an explicit reveal click and
// kept in component-local state — never in the Pinia store — so the secret
// lives in memory only while an administrator is looking at it.
const botUserToken = ref('');
const isFetchingToken = ref(false);

const resetForm = () => {
  Object.assign(form, emptyForm());
  editingId.value = null;
  showAdvanced.value = false;
  currentBotUser.value = null;
  botUserToken.value = '';
  isFetchingToken.value = false;
};

const revealBotUserToken = async () => {
  isFetchingToken.value = true;
  try {
    const { data } = await ChatbotsAPI.botUserToken(editingId.value);
    botUserToken.value = data.access_token;
  } catch (error) {
    useAlert(
      error?.response?.status === 404
        ? t('CHATBOTS.DIALOG.TOKEN.NO_BOT_USER')
        : t('CHATBOTS.DIALOG.TOKEN.ERROR')
    );
  } finally {
    isFetchingToken.value = false;
  }
};

const copyBotUserToken = async () => {
  await copyTextToClipboard(botUserToken.value);
  useAlert(t('CHATBOTS.DIALOG.TOKEN.COPIED'));
};

// Fills in only fields the tenant actually has a value for — a blank
// upstream field leaves whatever the form already had (its default, or the
// chatbot's own saved value) rather than clearing it, the same "don't
// overwrite with nothing" rule the write path's own upsert already follows.
const applyTenantBotConfig = ({ name, config } = {}) => {
  if (name) form.botName = name;
  if (!config) return;
  if (config.personality) form.personality = config.personality;
  if (config.emojiUsage) form.emojiUsage = config.emojiUsage;
  if (config.customerTreatment) {
    form.customerTreatment = config.customerTreatment;
  }
  if (config.botFunction) form.botFunction = config.botFunction;
  if (config.mainObjective) form.mainObjective = config.mainObjective;
  if (config.restrictions) form.restrictions = config.restrictions;
  if (config.valueProposition) form.valueProposition = config.valueProposition;
  if (config.botProcedure) form.botProcedure = config.botProcedure;
  if (config.schedulingUrl) form.schedulingUrl = config.schedulingUrl;
};

const loadTenantBotConfig = async () => {
  tenantBotConfigRequestId += 1;
  const requestId = tenantBotConfigRequestId;
  const tenantBotConfig = await chatbotsStore.fetchTenantBotConfig();
  if (requestId !== tenantBotConfigRequestId) return;
  applyTenantBotConfig(tenantBotConfig);
};

const openForCreate = () => {
  resetForm();
  chatbotsStore.fetchAssignableBotUsers();
  loadTenantBotConfig();
  dialogRef.value?.open();
};

const openForEdit = bot => {
  resetForm();
  editingId.value = bot.id;
  currentBotUser.value = bot.botUser || null;
  Object.assign(form, {
    botName: bot.botName || '',
    botFunction: bot.botFunction || '',
    personality: bot.personality || PERSONALITY_OPTIONS[0],
    emojiUsage: bot.emojiUsage || EMOJI_OPTIONS[0],
    customerTreatment: bot.customerTreatment || TREATMENT_OPTIONS[0],
    mainObjective: bot.mainObjective || '',
    valueProposition: bot.valueProposition || '',
    restrictions: bot.restrictions || '',
    botProcedure: bot.botProcedure || '',
    schedulingUrl: bot.schedulingUrl || '',
    connectors: Array.isArray(bot.connectors) ? [...bot.connectors] : [],
    botUserId: bot.botUserId || null,
  });
  // Reveal Advanced when it already has content.
  showAdvanced.value = !!(
    form.restrictions ||
    form.botProcedure ||
    form.connectors.length
  );
  chatbotsStore.fetchAssignableBotUsers();
  loadTenantBotConfig();
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;
  emit('submit', {
    ...(isEditing.value ? { id: editingId.value } : {}),
    botName: form.botName.trim(),
    botFunction: form.botFunction.trim(),
    personality: form.personality,
    emojiUsage: form.emojiUsage,
    customerTreatment: form.customerTreatment,
    mainObjective: form.mainObjective.trim(),
    valueProposition: form.valueProposition.trim(),
    restrictions: form.restrictions.trim(),
    botProcedure: form.botProcedure.trim(),
    schedulingUrl: form.schedulingUrl.trim(),
    connectors: form.connectors,
    botUserId: form.botUserId,
  });
};

const closeDialog = () => dialogRef.value?.close();
const onSuccess = () => {
  resetForm();
  closeDialog();
};

defineExpose({ openForCreate, openForEdit, onSuccess });

const selectClass =
  'w-full h-9 px-3 rounded-lg text-sm bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand';
</script>

<template>
  <Dialog ref="dialogRef" width="3xl" overflow-y-auto @close="resetForm">
    <div class="flex flex-col gap-5">
      <span class="text-sm font-medium text-n-slate-12">
        {{
          isEditing
            ? t('CHATBOTS.DIALOG.EDIT_TITLE')
            : t('CHATBOTS.DIALOG.CREATE_TITLE')
        }}
      </span>

      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ t('CHATBOTS.DIALOG.NAME') }}
          </span>
          <Input
            v-model="form.botName"
            :placeholder="t('CHATBOTS.DIALOG.NAME_PLACEHOLDER')"
            :disabled="isLoading"
            autofocus
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ t('CHATBOTS.DIALOG.BOT_USER') }}
          </span>
          <select
            v-if="botUserOptions.length"
            v-model="form.botUserId"
            :class="selectClass"
          >
            <option :value="null">
              {{ t('CHATBOTS.DIALOG.BOT_USER_NONE') }}
            </option>
            <option
              v-for="user in botUserOptions"
              :key="user.id"
              :value="user.id"
            >
              {{ user.name }}
            </option>
          </select>
          <span v-else class="text-xs text-n-slate-10">
            {{ t('CHATBOTS.DIALOG.BOT_USER_EMPTY') }}
          </span>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ t('CHATBOTS.DIALOG.FUNCTION') }}
          </span>
          <Input
            v-model="form.botFunction"
            :placeholder="t('CHATBOTS.DIALOG.FUNCTION_PLACEHOLDER')"
            :disabled="isLoading"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ t('CHATBOTS.DIALOG.PERSONALITY') }}
          </span>
          <select v-model="form.personality" :class="selectClass">
            <option v-for="opt in PERSONALITY_OPTIONS" :key="opt" :value="opt">
              {{ t(PERSONALITY_LABEL_KEYS[opt]) }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ t('CHATBOTS.DIALOG.EMOJI_USAGE') }}
          </span>
          <select v-model="form.emojiUsage" :class="selectClass">
            <option v-for="opt in EMOJI_OPTIONS" :key="opt" :value="opt">
              {{ t(EMOJI_LABEL_KEYS[opt]) }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ t('CHATBOTS.DIALOG.CUSTOMER_TREATMENT') }}
          </span>
          <select v-model="form.customerTreatment" :class="selectClass">
            <option v-for="opt in TREATMENT_OPTIONS" :key="opt" :value="opt">
              {{ t(TREATMENT_LABEL_KEYS[opt]) }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ t('CHATBOTS.DIALOG.SCHEDULING_URL') }}
          </span>
          <Input
            v-model="form.schedulingUrl"
            :placeholder="t('CHATBOTS.DIALOG.SCHEDULING_URL_PLACEHOLDER')"
            :disabled="isLoading"
          />
        </label>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('CHATBOTS.DIALOG.MAIN_OBJECTIVE') }}
        </span>
        <TextArea
          v-model="form.mainObjective"
          :placeholder="t('CHATBOTS.DIALOG.MAIN_OBJECTIVE_PLACEHOLDER')"
          :disabled="isLoading"
          :max-length="500"
          class="w-full"
          auto-height
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('CHATBOTS.DIALOG.VALUE_PROPOSITION') }}
        </span>
        <TextArea
          v-model="form.valueProposition"
          :placeholder="t('CHATBOTS.DIALOG.VALUE_PROPOSITION_PLACEHOLDER')"
          :disabled="isLoading"
          :max-length="500"
          class="w-full"
          auto-height
        />
      </label>

      <div v-if="isEditing && isAdmin" class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('CHATBOTS.DIALOG.TOKEN.LABEL') }}
        </span>
        <div class="flex items-center gap-2">
          <template v-if="botUserToken">
            <code
              class="flex-1 px-3 py-2 overflow-x-auto text-xs rounded-lg bg-n-alpha-black2 text-n-slate-12 whitespace-nowrap"
            >
              {{ botUserToken }}
            </code>
            <Button
              :label="t('CHATBOTS.DIALOG.TOKEN.COPY')"
              color="blue"
              variant="faded"
              size="sm"
              @click="copyBotUserToken"
            />
          </template>
          <Button
            v-else
            :label="t('CHATBOTS.DIALOG.TOKEN.REVEAL')"
            color="slate"
            variant="faded"
            size="sm"
            :is-loading="isFetchingToken"
            @click="revealBotUserToken"
          />
        </div>
        <span class="text-xs text-n-slate-10">
          {{ t('CHATBOTS.DIALOG.TOKEN.HINT') }}
        </span>
      </div>

      <!-- Advanced — Super Admin only, toggle included -->
      <div
        v-if="isSuperAdmin"
        class="flex flex-col gap-4 pt-1 border-t border-n-weak"
      >
        <button
          type="button"
          class="flex items-center justify-between w-full pt-2 text-sm font-medium text-n-slate-12"
          @click="showAdvanced = !showAdvanced"
        >
          <span class="flex flex-col items-start">
            {{ t('CHATBOTS.DIALOG.ADVANCED') }}
            <span class="text-xs font-normal text-n-slate-10">
              {{ t('CHATBOTS.DIALOG.ADVANCED_HINT') }}
            </span>
          </span>
          <span
            class="size-4 text-n-slate-10"
            :class="
              showAdvanced ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
          />
        </button>

        <template v-if="showAdvanced">
          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ t('CHATBOTS.DIALOG.RESTRICTIONS') }}
            </span>
            <TextArea
              v-model="form.restrictions"
              :placeholder="t('CHATBOTS.DIALOG.RESTRICTIONS_PLACEHOLDER')"
              :disabled="isLoading"
              :max-length="500"
              class="w-full"
              auto-height
            />
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ t('CHATBOTS.DIALOG.BOT_PROCEDURE') }}
            </span>
            <TextArea
              v-model="form.botProcedure"
              :placeholder="t('CHATBOTS.DIALOG.BOT_PROCEDURE_PLACEHOLDER')"
              :disabled="isLoading"
              :max-length="500"
              class="w-full"
              auto-height
            />
          </label>

          <ChatbotConnectors
            v-model="form.connectors"
            :chatbot-id="editingId"
          />
        </template>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('CHATBOTS.DIALOG.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          :label="
            isEditing ? t('CHATBOTS.DIALOG.SAVE') : t('CHATBOTS.DIALOG.CREATE')
          "
          color="blue"
          type="submit"
          :disabled="isFormInvalid || isLoading"
          :is-loading="isLoading"
          @click="handleConfirm"
        />
      </div>
    </template>
  </Dialog>
</template>
